import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Users, DollarSign, Copy, Check, Clock, Bell, CreditCard, Edit3,
  Sparkles, AlertCircle, Loader2, RefreshCw, X, Save,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Localized strings — kept inside this component so it can be used standalone or
// embedded inside the regular user dashboard.
const copy = {
  fr: {
    title: 'Espace partenaire',
    welcome: 'Bonjour',
    subtitle: 'Suivez vos référés et vos commissions',
    yourCode: 'Votre code',
    yourLink: 'Votre lien de parrainage',
    copy: 'Copier',
    copied: 'Copié !',
    kpiTotal: 'Total référés',
    kpiPending: 'En attente',
    kpiApproved: 'Approuvés',
    kpiPaid: 'Payés',
    kpiEarned: 'Commissions gagnées',
    kpiPending2: 'Paiement en attente',
    kpiPaidOut: 'Payé',
    commissionRate: 'Taux de commission',
    perReferral: 'par référé payé',
    referrals: 'Mes Référés',
    refresh: 'Actualiser',
    name: 'Nom',
    email: 'Courriel',
    phone: 'Téléphone',
    status: 'Statut',
    age: 'Âge',
    actions: 'Actions',
    edit: 'Modifier',
    remind: 'Rappeler à l\'admin',
    payForUser: 'Payer pour cet utilisateur',
    statusPending: 'En attente',
    statusApproved: 'Approuvée',
    statusPaid: 'Payée',
    statusRejected: 'Rejetée',
    statusIncomplete: 'Incomplète',
    statusNone: 'Pas encore inscrit',
    noReferrals: 'Aucun référé pour le moment. Partagez votre lien !',
    editTitle: 'Modifier l\'inscription du référé',
    save: 'Enregistrer',
    cancel: 'Annuler',
    saving: 'Enregistrement…',
    saved: 'Mises à jour enregistrées.',
    payouts: 'Historique des Commissions',
    payoutPending: 'En attente',
    payoutPaid: 'Payé',
    noPayouts: 'Aucune commission encore.',
    daysAgo: (n) => `il y a ${n}j`,
    hoursAgo: (n) => `il y a ${n}h`,
    minutesAgo: (n) => `il y a ${n}min`,
    proxyHelp: 'Pour les utilisateurs sans carte bancaire — vous payez les 499 $ et leur inscription est validée.',
    remindHelp: 'Disponible après 72 heures d\'attente.',
    remindSuccess: 'Rappel envoyé aux administrateurs.',
    payRedirect: 'Redirection vers le paiement…',
  },
  en: {
    title: 'Partner Portal',
    welcome: 'Hi',
    subtitle: 'Track your referrals and earnings',
    yourCode: 'Your code',
    yourLink: 'Your referral link',
    copy: 'Copy',
    copied: 'Copied!',
    kpiTotal: 'Total referrals',
    kpiPending: 'Pending',
    kpiApproved: 'Approved',
    kpiPaid: 'Paid',
    kpiEarned: 'Total earned',
    kpiPending2: 'Pending payout',
    kpiPaidOut: 'Paid out',
    commissionRate: 'Commission rate',
    perReferral: 'per paid referral',
    referrals: 'My Referrals',
    refresh: 'Refresh',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    status: 'Status',
    age: 'Age',
    actions: 'Actions',
    edit: 'Edit',
    remind: 'Remind admin',
    payForUser: 'Pay for this user',
    statusPending: 'Pending',
    statusApproved: 'Approved',
    statusPaid: 'Paid',
    statusRejected: 'Rejected',
    statusIncomplete: 'Incomplete',
    statusNone: 'Not yet registered',
    noReferrals: 'No referrals yet. Share your link!',
    editTitle: 'Edit Referral Registration',
    save: 'Save',
    cancel: 'Cancel',
    saving: 'Saving…',
    saved: 'Updates saved.',
    payouts: 'Commission History',
    payoutPending: 'Pending',
    payoutPaid: 'Paid',
    noPayouts: 'No commissions yet.',
    daysAgo: (n) => `${n}d ago`,
    hoursAgo: (n) => `${n}h ago`,
    minutesAgo: (n) => `${n}m ago`,
    proxyHelp: 'For users without a credit card — you pay the $499 and their registration is settled.',
    remindHelp: 'Available after the registration has been pending for 72 hours.',
    remindSuccess: 'Reminder sent to admins.',
    payRedirect: 'Redirecting to checkout…',
  },
};

function timeAgo(iso, t) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return t.minutesAgo(0);
  if (secs < 3600) return t.minutesAgo(Math.floor(secs / 60));
  if (secs < 86400) return t.hoursAgo(Math.floor(secs / 3600));
  return t.daysAgo(Math.floor(secs / 86400));
}

function hoursSince(iso) {
  if (!iso) return 0;
  const then = new Date(iso).getTime();
  return Math.max(0, Math.floor((Date.now() - then) / 3600000));
}

/**
 * Reusable "Partner Portal" section. Render it as a full standalone page (see PartnerDashboard.jsx)
 * or embed it as a tab inside the regular user dashboard (Dashboard.jsx).
 *
 * Props:
 *   user, token, language — required (passed down from parent)
 *   showHeader (default true) — show the welcome card + referral link section
 */
export default function PartnerPortalSection({ user, token, language = 'fr', showHeader = true }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const t = copy[language] || copy.fr;
  const [me, setMe] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState(null);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (!token) return;
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Friendly toast after Square checkout redirect
  useEffect(() => {
    if (searchParams.get('proxy') === 'success') {
      setToast({ kind: 'success', text: language === 'fr' ? 'Paiement reçu. Vérification en cours…' : 'Payment received. Verifying…' });
      const next = new URLSearchParams(searchParams);
      next.delete('proxy');
      next.delete('reg');
      setSearchParams(next, { replace: true });
      setTimeout(fetchAll, 2500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [meRes, refsRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/partner/me`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/partner/referrals`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/partner/stats`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setMe(meRes.data);
      setReferrals(refsRes.data || []);
      setStats(statsRes.data || null);
    } catch (err) {
      setToast({ kind: 'error', text: err?.response?.data?.detail || 'Failed to load partner data' });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!me?.referral_link) return;
    try {
      await navigator.clipboard.writeText(me.referral_link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      setToast({ kind: 'error', text: 'Copy failed — long-press the link to copy.' });
    }
  };

  const handleRemind = async (registration_id) => {
    if (!window.confirm(language === 'fr' ? 'Envoyer un rappel à l\'admin pour ce référé ?' : 'Send a reminder to the admin for this referral?')) return;
    setBusyId(registration_id);
    try {
      await axios.post(`${BACKEND_URL}/api/partner/referrals/${registration_id}/remind-admin`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setToast({ kind: 'success', text: t.remindSuccess });
      fetchAll();
    } catch (err) {
      setToast({ kind: 'error', text: err?.response?.data?.detail || 'Failed to send reminder' });
    } finally {
      setBusyId(null);
    }
  };

  const handlePayForUser = async (registration_id) => {
    if (!window.confirm(language === 'fr' ? 'Procéder au paiement de 499 $ pour ce référé ?' : 'Proceed to pay $499 for this referral?')) return;
    setBusyId(registration_id);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/partner/payments/${registration_id}/create-link`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data?.checkout_url) {
        setToast({ kind: 'info', text: t.payRedirect });
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      setToast({ kind: 'error', text: err?.response?.data?.detail || 'Failed to create payment' });
    } finally {
      setBusyId(null);
    }
  };

  const handleStartEdit = (referral) => {
    if (!referral.registration) return;
    setEditing({
      registration_id: referral.registration.id,
      first_name: referral.first_name || '',
      last_name: referral.last_name || '',
      phone: referral.phone || '',
      passport_number: referral.registration.passport_number || '',
      passport_expiry: referral.registration.passport_expiry || '',
      date_of_birth: referral.registration.date_of_birth || '',
      address: referral.registration.address || '',
      city: referral.registration.city || '',
      country: referral.registration.country || '',
      occupation: referral.registration.occupation || '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    const { registration_id, ...fields } = editing;
    setBusyId(registration_id);
    try {
      await axios.patch(
        `${BACKEND_URL}/api/partner/referrals/${registration_id}`,
        fields,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setToast({ kind: 'success', text: t.saved });
      setEditing(null);
      fetchAll();
    } catch (err) {
      setToast({ kind: 'error', text: err?.response?.data?.detail || 'Failed to save changes' });
    } finally {
      setBusyId(null);
    }
  };

  const counts = stats?.referral_counts || {};
  const earnings = stats?.earnings || {};
  const payouts = stats?.payouts || [];

  const kpis = useMemo(() => ([
    { k: 'total', icon: Users, color: 'from-blue-500 to-blue-600', label: t.kpiTotal, value: counts.total || 0 },
    { k: 'pending', icon: Clock, color: 'from-amber-500 to-orange-500', label: t.kpiPending, value: counts.pending || 0 },
    { k: 'approved', icon: Sparkles, color: 'from-emerald-500 to-green-600', label: t.kpiApproved, value: counts.approved || 0 },
    { k: 'paid', icon: CreditCard, color: 'from-teal-500 to-cyan-600', label: t.kpiPaid, value: counts.paid || 0 },
    { k: 'earned', icon: DollarSign, color: 'from-[#D4AF37] to-amber-600', label: t.kpiEarned, value: `$${(earnings.total_earned_usd || 0).toFixed(2)}` },
    { k: 'pending2', icon: Loader2, color: 'from-orange-500 to-red-500', label: t.kpiPending2, value: `$${(earnings.pending_payout_usd || 0).toFixed(2)}` },
    { k: 'paidout', icon: Check, color: 'from-purple-500 to-fuchsia-600', label: t.kpiPaidOut, value: `$${(earnings.paid_out_usd || 0).toFixed(2)}` },
  ]), [counts, earnings, t]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="partner-section-loading">
        <Loader2 className="w-10 h-10 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="partner-portal-section">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-20 right-5 z-50 px-4 py-3 rounded-xl shadow-lg max-w-md text-sm font-semibold ${
          toast.kind === 'error' ? 'bg-red-50 text-red-700 border border-red-200' :
          toast.kind === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' :
          'bg-blue-50 text-blue-800 border border-blue-200'
        }`}>
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{toast.text}</span>
            <button onClick={() => setToast(null)} className="ml-2 opacity-60 hover:opacity-100"><X className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      )}

      {showHeader && (
        <section className="rounded-2xl bg-gradient-to-br from-[#1D1D1D] via-[#0B0E13] to-[#1a120a] p-6 text-white shadow-xl relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-amber-500/20 blur-3xl pointer-events-none" />
          <div className="relative grid md:grid-cols-3 gap-5">
            <div className="md:col-span-2">
              <p className="text-[12px] font-bold text-amber-400 uppercase tracking-[0.16em] mb-1.5 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" /> {t.welcome}, {me?.first_name || user?.first_name}
              </p>
              <h1 className="text-2xl md:text-3xl font-extrabold leading-tight">{t.title}</h1>
              <p className="text-gray-400 text-sm mt-1.5">{t.subtitle}</p>
              <div className="mt-5 bg-white/5 border border-white/10 rounded-xl p-4">
                <p className="text-[10px] text-amber-400 font-bold uppercase tracking-wider mb-1">{t.yourCode}</p>
                <div className="flex items-center gap-2 mb-3">
                  <code data-testid="partner-referral-code" className="font-mono text-lg font-bold text-white">{me?.referral_code}</code>
                </div>
                <p className="text-[10px] text-amber-400 font-bold uppercase tracking-wider mb-1">{t.yourLink}</p>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <code data-testid="partner-referral-link" className="font-mono text-xs text-amber-100 bg-black/30 px-3 py-2 rounded flex-1 truncate">{me?.referral_link}</code>
                  <button
                    data-testid="partner-copy-link-btn"
                    onClick={handleCopyLink}
                    className="px-3 py-2 bg-amber-500 hover:bg-amber-400 text-black rounded-lg font-bold text-xs flex items-center gap-1.5 transition"
                  >
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? t.copied : t.copy}
                  </button>
                </div>
              </div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-5 flex flex-col justify-center">
              <p className="text-[10px] text-amber-400 font-bold uppercase tracking-wider mb-1">{t.commissionRate}</p>
              <p className="text-3xl font-extrabold text-amber-300">{((earnings.rate || 0) * 100).toFixed(2)}%</p>
              <p className="text-xs text-gray-400 mt-1">
                <span className="font-bold text-white">${(earnings.commission_per_referral_usd || 0).toFixed(2)}</span> {t.perReferral}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* KPI grid */}
      <section className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {kpis.map((k) => (
          <div key={k.k} data-testid={`partner-kpi-${k.k}`} className="bg-white rounded-xl border border-gray-200/70 p-4 hover:border-gray-300 hover:shadow-sm transition">
            <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${k.color} flex items-center justify-center mb-3 shadow-sm`}>
              <k.icon className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">{k.label}</p>
            <p className="font-extrabold text-[#1D1D1D] leading-tight text-2xl mt-0.5">{k.value}</p>
          </div>
        ))}
      </section>

      {/* Referrals table */}
      <section className="bg-white rounded-2xl border border-gray-200/70 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-extrabold text-[14px] text-[#1D1D1D]">{t.referrals}</h2>
          <button
            data-testid="partner-refresh-btn"
            onClick={fetchAll}
            className="text-xs font-bold text-gray-500 hover:text-[#CD0000] flex items-center gap-1"
          >
            <RefreshCw className="w-3.5 h-3.5" /> {t.refresh}
          </button>
        </div>
        {referrals.length === 0 ? (
          <div className="px-5 py-10 text-center text-gray-400 text-sm">{t.noReferrals}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase">{t.name}</th>
                  <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase">{t.email}</th>
                  <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase">{t.status}</th>
                  <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase">{t.age}</th>
                  <th className="px-5 py-3 text-right text-xs font-bold text-gray-500 uppercase">{t.actions}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {referrals.map((r) => {
                  const reg = r.registration;
                  const status = reg?.status;
                  const age = hoursSince(reg?.created_at);
                  const canRemind = status === 'pending' && age >= 72;
                  const canPay = status === 'approved';
                  const canEdit = !!reg && status !== 'paid';
                  return (
                    <tr key={r.id} data-testid={`partner-referral-row-${r.id}`} className="hover:bg-gray-50">
                      <td className="px-5 py-3 text-sm font-medium text-[#1D1D1D]">{r.first_name} {r.last_name}</td>
                      <td className="px-5 py-3 text-sm text-gray-500">{r.email}</td>
                      <td className="px-5 py-3"><StatusPill status={status} t={t} /></td>
                      <td className="px-5 py-3 text-xs text-gray-500 tabular-nums">{reg?.created_at ? timeAgo(reg.created_at, t) : '—'}</td>
                      <td className="px-5 py-3">
                        <div className="flex gap-2 flex-wrap justify-end">
                          {canEdit && (
                            <button
                              data-testid={`partner-edit-${r.id}`}
                              onClick={() => handleStartEdit(r)}
                              className="text-[#CD0000] hover:underline text-sm flex items-center gap-1"
                            >
                              <Edit3 className="w-3.5 h-3.5" /> {t.edit}
                            </button>
                          )}
                          {status === 'pending' && (
                            <button
                              data-testid={`partner-remind-${r.id}`}
                              onClick={() => handleRemind(reg.id)}
                              disabled={!canRemind || busyId === reg.id}
                              title={canRemind ? '' : t.remindHelp}
                              className="text-amber-700 hover:underline text-sm flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <Bell className="w-3.5 h-3.5" /> {t.remind}
                            </button>
                          )}
                          {canPay && (
                            <button
                              data-testid={`partner-pay-${r.id}`}
                              onClick={() => handlePayForUser(reg.id)}
                              disabled={busyId === reg.id}
                              title={t.proxyHelp}
                              className="text-emerald-700 hover:underline text-sm flex items-center gap-1 disabled:opacity-40"
                            >
                              <CreditCard className="w-3.5 h-3.5" /> {t.payForUser}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Commission history */}
      <section className="bg-white rounded-2xl border border-gray-200/70 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="font-extrabold text-[14px] text-[#1D1D1D]">{t.payouts}</h2>
        </div>
        {payouts.length === 0 ? (
          <div className="px-5 py-10 text-center text-gray-400 text-sm">{t.noPayouts}</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {payouts.map((p) => (
              <li key={p.id} data-testid={`partner-payout-${p.id}`} className="px-5 py-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-amber-50 flex items-center justify-center shrink-0">
                  <DollarSign className="w-4 h-4 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#1D1D1D]">${(p.commission_usd || 0).toFixed(2)} USD</p>
                  <p className="text-xs text-gray-500">{timeAgo(p.created_at, t)}</p>
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md border ${
                  p.status === 'paid'
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'bg-amber-50 text-amber-700 border-amber-200'
                }`}>
                  {p.status === 'paid' ? t.payoutPaid : t.payoutPending}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Edit modal */}
      {editing && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setEditing(null)}
          data-testid="partner-edit-modal"
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="text-base font-extrabold text-[#1D1D1D] flex items-center gap-2">
                <Edit3 className="w-4 h-4 text-[#CD0000]" /> {t.editTitle}
              </h3>
              <button onClick={() => setEditing(null)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto">
              {[
                ['first_name', t.name + ' (1)'],
                ['last_name', t.name + ' (2)'],
                ['phone', t.phone],
                ['date_of_birth', 'DOB'],
                ['passport_number', 'Passport #'],
                ['passport_expiry', 'Passport expiry'],
                ['address', 'Address'],
                ['city', 'City'],
                ['country', 'Country'],
                ['occupation', 'Occupation'],
              ].map(([field, label]) => (
                <div key={field} className={field === 'address' ? 'col-span-2' : ''}>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">{label}</label>
                  <input
                    data-testid={`partner-edit-${field}`}
                    type="text"
                    value={editing[field] || ''}
                    onChange={(e) => setEditing((p) => ({ ...p, [field]: e.target.value }))}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#CD0000]"
                  />
                </div>
              ))}
            </div>
            <div className="px-5 py-4 border-t border-gray-100 flex gap-2 justify-end">
              <button onClick={() => setEditing(null)} className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold px-4 py-2 rounded-lg text-sm">{t.cancel}</button>
              <button
                data-testid="partner-edit-save-btn"
                onClick={handleSaveEdit}
                disabled={busyId === editing.registration_id}
                className="bg-[#CD0000] hover:bg-[#A00000] disabled:opacity-60 text-white font-bold px-4 py-2 rounded-lg flex items-center gap-2 text-sm"
              >
                {busyId === editing.registration_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {busyId === editing.registration_id ? t.saving : t.save}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusPill({ status, t }) {
  const map = {
    pending: { label: t.statusPending, cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    approved: { label: t.statusApproved, cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    paid: { label: t.statusPaid, cls: 'bg-teal-50 text-teal-700 border-teal-200' },
    rejected: { label: t.statusRejected, cls: 'bg-rose-50 text-rose-700 border-rose-200' },
    incomplete: { label: t.statusIncomplete, cls: 'bg-orange-50 text-orange-700 border-orange-200' },
  };
  const meta = map[status] || { label: t.statusNone, cls: 'bg-gray-50 text-gray-600 border-gray-200' };
  return (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md border ${meta.cls}`}>{meta.label}</span>
  );
}
